/**
 * CelloSign Iframe Configuration Registry
 *
 * Each entry defines a CelloSign iframe type used by CellosignIframe.vue.
 * The route /cellosign-iframe/:type/:id? resolves the `type` param
 * against this map to drive title, navigation, data loading, and payload.
 *
 * To add a new iframe type, add a keyed entry with:
 * @property {string}   title            - Page heading shown to the user.
 * @property {string}   backText         - Label for the back button.
 * @property {string}   endpoint         - API path suffix passed to sendCelloSign.
 * @property {string|null} template      - CelloSign template UUID, or null if not needed.
 * @property {Function} backRoute        - Returns the route string to navigate back to,
 *                                         or null to use router.go(-1) (mobile fallback).
 * @property {Function} loadData(vm)     - Async. Receives the Vue instance.
 *                                         Fetches event/patient data and returns the
 *                                         form payload object, or null when no id is present.
 * @property {Function} buildBasePayload(vm) - Returns the base doctor fields merged
 *                                             into every request before formaData.
 * @property {Object}  [generic]         - When present, the iframe is fetched via the
 *                                         unified /cellosign-generic endpoint instead of
 *                                         the legacy per-form endpoint. Base + form data
 *                                         are merged into `data` (plus wait_for_first_session).
 * @property {string}   generic.url      - CelloSign API URL to call (project-scoped seal URL).
 * @property {string}  [generic.project] - CelloSign project name. Omit to use the backend
 *                                         default (doc_app_forms).
 */

import { getHash } from "./hash.js";

/** Hospital procedure codes that classify a colonoscopy. */
const COLONOSCOPY_CODES = ["5203"];

/** Hospital procedure codes that classify a gastroscopy. */
const GASTROSCOPY_CODES = ["5205"];

/**
 * @param {Object[]} orders - Surgery orders from getSurgeryOrders.
 * @param {string[]} codeList - Hospital procedure codes to match against.
 * @returns {boolean} True if at least one order matches a code in the list.
 */
function hasCodeMatch(orders, codeList) {
  if (!orders || !orders.length) return false;
  return orders.some(s => s.tafnit_code && codeList.includes(s.tafnit_code));
}

/**
 * Builds CelloSign `procedures` as a JSON string: a 2D array of
 * `[surgery name, ICD-9 code]` per order. Orders with tafnit_code 9000 are skipped.
 * Empty orders → `"[]"`.
 *
 * @param {Object[]|null|undefined} orders - Surgery orders from getSurgeryOrders.
 * @returns {string}
 */
function buildProceduresTableJson(orders) {
  const rows = [];
  if (orders && orders.length) {
    for (const s of orders) {
      if (s.tafnit_code === "9000") continue;
      rows.push([s.type || "", s.icd9_procedure || ""]);
    }
  }
  return JSON.stringify(rows);
}

export const iframeConfigs = {
  /**
   * Exception-committee form (וועדת חריגים).
   * Opened from the surgeries calendar / dots menu / surgery info page.
   * Loads surgery + patient data via getSurgeryInfo.
   */
  "exception-committee": {
    title: "הגשה לוועדת חריגים",
    backText: "חזור ליומן הניתוחים",
    endpoint: "exception-committee-iframe",
    template: "476423d4ce3348cc9e15a98185ff9055",
    generic: {
      url:
        "https://app.cellosign.com/api/v3/doc_app_forms/bpmn/e845ac7c-79cb-4e73-8c54-0ba315012d70/start/",
      project: "doc_app_forms"
    },
    backRoute() {
      const isMobile = window.innerWidth < 1200;
      if (isMobile) return null;
      const hashData = JSON.parse(localStorage.getItem("hashData"));
      if (!hashData || !Object.keys(hashData).length) {
        return "/dashboard/surgeries";
      }
      return "/dashboard/surgeries#" + getHash({});
    },
    async loadData(vm) {
      const id = vm.$route.params.id;
      if (!id) return null;

      const res = await vm.$API.getSurgeryInfo(id);
      const { patient, surgery, doctor } = res.message;

      let birthDate;
      if (patient.birthDate) {
        birthDate = vm
          .$moment(patient.birthDate, "YYYY-MM-DD")
          .format("DD/MM/YYYY");
      }

      let startDate;
      if (surgery.start_date) {
        startDate = vm
          .$moment(surgery.start_date, "YYYY-MM-DDTHH:mm:ss")
          .format("DD/MM/YYYY");
      }

      return {
        obj_id: `surgery_${id}`,
        doctor_name: doctor.heb_name,
        firstName: patient.firstName,
        lastName: patient.lastName,
        passport: patient.passport,
        start_date: startDate,
        surgery_name: surgery.name,
        hospitalization: surgery.hospitalization,
        birthDate: birthDate,
        FTzpH2DJdnUe: "",
        reason: "",
        formtype: "exception-committee-form"
      };
    },
    buildBasePayload(vm) {
      return {
        doccode: vm.activeDoctor.license,
        mdbid: vm.activeDoctor.id
      };
    }
  },

  /**
   * Pathology results summary (סיכום תוצאות פתולוגיה).
   * Opened from the pathology documents table when no summary exists yet.
   * Loads surgery + patient data and surgery orders to determine
   * colonoscopy/gastroscopy flags and procedure list.
   */
  "pathology-summary": {
    title: "סיכום תוצאות פתולוגיה",
    backText: "חזור לטבלת פתולוגיה",
    endpoint: "pathology-summary-iframe",
    template: "763ed74d-7ab3-458b-92e3-caf7eb4bb062",
    generic: {
      url:
        "https://app.cellosign.com/api/v3/doc_app_forms/bpmn/763ed74d-7ab3-458b-92e3-caf7eb4bb062/start/",
      project: "doc_app_forms"
    },
    backRoute() {
      const isMobile = window.innerWidth < 1200;
      if (isMobile) return null;
      return "/table/pathology";
    },
    async loadData(vm) {
      const eventId = vm.$route.params.id;
      if (!eventId) return null;

      const eventType = vm.$route.query.type;

      const formatLocalDate = dateStr => {
        if (!dateStr) return "";
        const m = vm.$moment(dateStr, [
          "YYYY-MM-DDTHH:mm:ss",
          "YYYY-MM-DD HH:mm:ss",
          "YYYY-MM-DDTHH:mm",
          "YYYY-MM-DD HH:mm"
        ]);
        if (!m.isValid()) return "";
        return m.format("DD/MM/YYYY");
      };

      if (eventType === "appointment") {
        const res = await vm.$API.getAppointmentInfo(eventId);
        if (!res || res.status !== "OK") {
          throw new Error("לא ניתן לטעון את פרטי האירוע");
        }

        const { appointment } = res.message;

        return {
          obj_id: `appointment_${eventId}`,
          firstName: appointment.firstName || "",
          lastName: appointment.lastName || "",
          passport: appointment.passport || "",
          tafnit_file_no: appointment.tafnit_file_no || "",
          parent_name: appointment.apotropos || appointment.parent_name || "",
          birthDate: appointment.birthDate
            ? vm
                .$moment(appointment.birthDate, "YYYY-MM-DD")
                .format("DD/MM/YYYY")
            : "",
          phone: appointment.phone || "",
          start_date: formatLocalDate(appointment.start_datetime),
          email: appointment.email || "",
          gender: appointment.gender || "",
          address: appointment.address || "",
          insurance_name: appointment.insurance_name || "",
          colonoscopy: hasCodeMatch(orders, COLONOSCOPY_CODES),
          gastroscopy: hasCodeMatch(orders, GASTROSCOPY_CODES),
          procedures: buildProceduresTableJson(orders)
        };
      }

      const res = await vm.$API.getSurgeryInfo(eventId);
      if (!res || res.status !== "OK") {
        throw new Error("לא ניתן לטעון את פרטי האירוע");
      }

      const { patient, surgery } = res.message;

      const resOrders = await vm.$API.getSurgeryOrders(eventId);
      const orders =
        resOrders && resOrders.status === "OK" && resOrders.data?.orders
          ? resOrders.data.orders
          : [];

      return {
        obj_id: `surgery_${eventId}`,
        firstName: patient.firstName || "",
        lastName: patient.lastName || "",
        passport: patient.passport || "",
        tafnit_file_no: surgery.tafnit_file_no || "",
        parent_name: patient.apotropos || "",
        birthDate: patient.birthDate
          ? vm.$moment(patient.birthDate, "YYYY-MM-DD").format("DD/MM/YYYY")
          : "",
        phone: patient.phone || "",
        start_date: formatLocalDate(surgery.start_date),
        email: patient.email || "",
        gender: patient.gender || "",
        address: patient.address || "",
        insurance_name: surgery.insurance_name || "",
        colonoscopy: hasCodeMatch(orders, COLONOSCOPY_CODES),
        gastroscopy: hasCodeMatch(orders, GASTROSCOPY_CODES),
        procedures: buildProceduresTableJson(orders)
      };
    },
    buildBasePayload(vm) {
      return {
        heb_name: vm.activeDoctor.heb_name || vm.activeDoctor.name || "",
        license: vm.activeDoctor.license
      };
    }
  },

  /**
   * Clinic secretary access form (פתיחת גישה למזכירת מרפאה).
   * Opened from the main menu. Sends only the doctor's Hebrew name.
   */
  "secretary-access": {
    title: "פתיחת גישה למזכירת מרפאה",
    backText: "חזור לניהול מרפאה",
    endpoint: "secretary-access-iframe",
    template: "baad4773-fafe-4394-b114-d6cb5fe59483",
    generic: {
      url:
        "https://app.cellosign.com/api/v3/doc_app_forms/bpmn/baad4773-fafe-4394-b114-d6cb5fe59483/start/",
      project: "doc_app_forms"
    },
    backRoute() {
      const isMobile = window.innerWidth < 1200;
      if (isMobile) return null;
      return "/dashboard";
    },
    async loadData() {
      return null;
    },
    buildBasePayload(vm) {
      return {
        heb_name: vm.activeDoctor.heb_name || vm.activeDoctor.name || ""
      };
    }
  }
};
